import React, { useCallback } from 'react';

import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { isEqual } from 'lodash';
import { Moment } from 'moment';

import {
  ActionsRequisesDtoActionsRequisesEnum,
  TraitementResponseDtoListStatutEnum,
} from 'lib_api/lib/api/gen';

import { FilterButton } from 'components/WrappedComponents';
import DashboardSearchBarWithTooltip from 'components/WrappedComponents/SearchBar/DashboardSearchBarWithTooltip';
import { useFOFilterContext } from 'hooks/FOFilterStoreContext';

import { DashboardFOFilter, DashboardForcesOrdreFilterForm } from '../../types';

import FilterFormForces from './FilterFormForces';

import 'styles/DashboardHeaderFilter.less';

interface FiltersProps {
  filter: Partial<DashboardFOFilter>;
  setFilter: (value: Partial<DashboardFOFilter>) => void;
}

function keepDossierSortis(
  statut: TraitementResponseDtoListStatutEnum[],
  sortieFilter: boolean,
  dateRangeSortieDefinitive: [Moment | undefined, Moment | undefined],
  motifDeSortieFilter: string[],
  includeDossiersAnonymises: boolean,
  includeOnlyNotDetruitApresSorti: boolean,
): boolean {
  // automatically check the "sorti" checkbox
  return (
    // if the statut SORTI is selected
    statut.includes(TraitementResponseDtoListStatutEnum.SORTI) ||
    // if the checkbox is checked
    (statut.length === 0 && sortieFilter) ||
    // if we search for a date sortie
    !!dateRangeSortieDefinitive[0] ||
    !!dateRangeSortieDefinitive[1] ||
    motifDeSortieFilter.length !== 0 ||
    // if we search for anonymized dossiers
    includeDossiersAnonymises ||
    includeOnlyNotDetruitApresSorti
  );
}

function Filters({ filter, setFilter }: FiltersProps): React.ReactElement {
  const {
    includeDossiersSortis,
    includeOnlyActionsRequises,
    search,
    ...formFilter
  } = filter;
  const [, , foActionsRequises] = useFOFilterContext();

  const keepOnlyActionsRequises = useCallback(
    (actionsRequises?: ActionsRequisesDtoActionsRequisesEnum[]): boolean => {
      return isEqual(new Set(actionsRequises), new Set(foActionsRequises));
    },
    [foActionsRequises],
  );

  function setFormFilter(value: Partial<DashboardForcesOrdreFilterForm>) {
    const {
      statut,
      dateRangeSortieDefinitive,
      actionsRequises,
      motifDeSortie,
      includeDossiersAnonymises,
      includeOnlyNotDetruitApresSorti,
    } = value;
    setFilter({
      ...filter,
      ...value,
      includeOnlyNotDetruitApresSorti,
      includeDossiersSortis: keepDossierSortis(
        statut ?? [],
        filter.includeDossiersSortis ?? false,
        dateRangeSortieDefinitive ?? [undefined, undefined],
        motifDeSortie ?? [],
        includeDossiersAnonymises ?? false,
        includeOnlyNotDetruitApresSorti ?? false,
      ),
      includeOnlyActionsRequises: keepOnlyActionsRequises(actionsRequises),
    });
  }

  function setIncludeDossiersSortis(event: CheckboxChangeEvent) {
    const {
      statut,
      dateRangeSortieDefinitive,
      motifDeSortie,
      includeOnlyNotDetruitApresSorti,
    } = filter;
    const updatedStatut =
      statut?.filter(s => s !== TraitementResponseDtoListStatutEnum.SORTI) ||
      [];

    // Only add the SORTI statut filter if the statut filter is not empty
    if (event.target.checked && (statut?.length ?? -1) > 0) {
      updatedStatut.push(TraitementResponseDtoListStatutEnum.SORTI);
    }

    setFilter({
      ...filter,
      statut: updatedStatut,
      includeDossiersSortis: event.target.checked,
      motifDeSortie: event.target.checked ? motifDeSortie : [],
      // clear related filters when checkbox unchecked
      dateRangeSortieDefinitive: event.target.checked
        ? dateRangeSortieDefinitive
        : undefined,
      includeDossiersAnonymises: undefined,
      includeOnlyNotDetruitApresSorti: event.target.checked
        ? includeOnlyNotDetruitApresSorti
        : undefined,
    });
  }

  function setIncludeOnlyActionsRequises(event: CheckboxChangeEvent) {
    const isChecked = event.target.checked;
    setFilter({
      ...filter,
      actionsRequises: isChecked ? foActionsRequises : undefined,
      includeOnlyActionsRequises: isChecked,
    });
  }

  return (
    <div className="dashboard-header-filter">
      <DashboardSearchBarWithTooltip
        search={search}
        filter={filter}
        setFilter={setFilter}
      />
      <FilterButton
        filter={formFilter}
        setFilter={setFormFilter}
        textInactiveFilter="Filtres"
        textActiveFilter="Filtres (actifs)"
      >
        {(setData, data) => (
          <FilterFormForces filter={data} setFilter={setData} />
        )}
      </FilterButton>
      <Checkbox
        checked={includeDossiersSortis}
        onChange={setIncludeDossiersSortis}
      >
        Afficher les véhicules sortis
      </Checkbox>
      <Checkbox
        checked={includeOnlyActionsRequises}
        onChange={setIncludeOnlyActionsRequises}
      >
        Afficher uniquement les actions requises
      </Checkbox>
    </div>
  );
}

export default Filters;
